import React, { useState } from "react";
import { Link } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { auth } from "../firebase";
import { stripEmoji } from "../penpal";
import { isSlowDown } from "../pace";
import Icon from "./Icon";
import PromptIdea from "./PromptIdea";

// The postcard look and "write a post" form shared by the PenPal Board and
// the community rooms, so both stay identical.

export const pastelColors = [
  "#fff9c4",
  "#f8bbd0",
  "#c8e6c9",
  "#e1bee7",
  "#bbdefb",
  "#fce4ec",
  "#ffe0cc",
  "#d2f8f0",
  "#fad2e1",
  "#e9d3ff",
  "#b0bec5",
  "#a1887f",
  "#9575cd",
  "#6d4c41",
  "#455a64",
  "#37474f",
  "#8e24aa",
  "#1e1e1e",
];

export function isDarkColor(hex: string): boolean {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 140;
}

// Turn rich-text HTML into a short plain-text preview.
// DOMParser builds an inert document, so nothing in it runs.
export function toPlainText(html?: string): string {
  if (!html) return "";
  const spaced = html.replace(/<\/(p|li|h\d)>/g, "$& ");
  const text = new DOMParser().parseFromString(spaced, "text/html").body.textContent || "";
  return text.replace(/\s+/g, " ").trim();
}

// One postcard in a grid.
export function Postcard({
  to,
  subject,
  message,
  cw,
  color,
  categories,
  from,
  corner,
  footnote,
}: {
  to: string;
  subject: string;
  message?: string;
  cw?: string;
  color?: string;
  categories?: string[];
  from: string;
  corner?: React.ReactNode; // e.g. a pin button
  footnote?: string; // e.g. "3 comments"
}) {
  const dark = color ? isDarkColor(color) : false;
  const preview = toPlainText(message);
  return (
    <Link to={to} className={`postcard${dark ? " dark" : ""}`} style={{ background: color || "#fffaf0" }}>
      {corner}
      <span className="stamp" aria-hidden="true">
        <Icon name="heart" size={20} tone="pink" />
      </span>
      <h3 className="postcard-title">{subject}</h3>
      {cw ? (
        <p className="cw-tag">CW: {cw}</p>
      ) : (
        preview && (
          <p className="text-sm opacity-90">{preview.length > 110 ? preview.slice(0, 110) + "…" : preview}</p>
        )
      )}
      {categories && categories.length > 0 && (
        <div className="chip-row mt-3" style={{ clear: "both" }}>
          {categories.map((cat) => (
            <span key={cat} className="chip">
              {stripEmoji(cat)}
            </span>
          ))}
        </div>
      )}
      <p className="postcard-from">
        {from}
        {footnote && <span className="block text-xs opacity-80">{footnote}</span>}
      </p>
    </Link>
  );
}

export type ComposedPost = {
  subject: string;
  message: string; // rich-text HTML
  color: string;
  categories: string[];
  cw: string;
  anonymous: boolean;
};

// The "write a post" form: title, letter, content warning, paper color,
// up to 3 categories, and anonymous posting.
export function PostComposer({
  titleLabel,
  titlePlaceholder,
  bodyPlaceholder,
  categoryOptions,
  anonymousHint,
  submitLabel,
  onPost,
  ideas,
  initialCategories,
}: {
  titleLabel: string;
  titlePlaceholder: string;
  bodyPlaceholder: string;
  categoryOptions: string[];
  anonymousHint: React.ReactNode;
  submitLabel: string;
  onPost: (p: ComposedPost) => Promise<void>;
  ideas?: string[]; // writing prompts shown under the editor
  initialCategories?: string[]; // picked to start with
}) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [color, setColor] = useState(pastelColors[0]);
  const [categories, setCategories] = useState<string[]>(initialCategories || []);
  const [anonymous, setAnonymous] = useState(false);
  const [cw, setCw] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleCategory = (cat: string) => {
    if (categories.includes(cat)) setCategories(categories.filter((c) => c !== cat));
    else if (categories.length < 3) setCategories([...categories, cat]);
  };

  const post = async () => {
    const cleanText = message.replace(/<(.|\n)*?>/g, "").trim();
    if (!subject.trim() || !cleanText) return alert("Please fill out both fields.");
    setBusy(true);
    try {
      await onPost({ subject: subject.trim(), message, color, categories, cw: cw.trim().slice(0, 100), anonymous });
      setSubject("");
      setMessage("");
      setColor(pastelColors[0]);
      setCategories([]);
      setAnonymous(false);
      setCw("");
    } catch (err) {
      if (isSlowDown(err)) return setBusy(false); // the wait note is showing; keep their writing
      console.error("Failed to post:", err);
      alert(
        auth.currentUser?.emailVerified
          ? "Sorry, that couldn't be posted. Please try again."
          : "Please verify your email first. Use the reminder at the top of the page to resend the link."
      );
    }
    setBusy(false);
  };

  return (
    <div className="mt-4 flex flex-col gap-4">
      <label className="field">
        <span className="field-label">{titleLabel}</span>
        <input
          type="text"
          className="input"
          maxLength={120}
          placeholder={titlePlaceholder}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </label>

      <div className="field">
        <span className="field-label">Your letter</span>
        <div className="quill-paper">
          <ReactQuill
            value={message}
            onChange={setMessage}
            theme="snow"
            placeholder={bodyPlaceholder}
            modules={{
              toolbar: [
                [{ header: [1, 2, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link"],
                ["clean"],
              ],
            }}
          />
        </div>
        {ideas && ideas.length > 0 && <PromptIdea prompts={ideas} />}
      </div>

      <label className="field">
        <span className="field-label">Content warning (optional)</span>
        <input
          type="text"
          className="input"
          maxLength={100}
          placeholder="e.g. grief, medical stuff, family conflict"
          value={cw}
          onChange={(e) => setCw(e.target.value)}
        />
        <span className="text-xs text-ink-soft">
          If you add one, your post's text stays hidden until someone chooses to read it.
        </span>
      </label>

      <div className="field">
        <span className="field-label">Paper color</span>
        <div className="flex flex-wrap gap-2">
          {pastelColors.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Paper color ${c}`}
              aria-pressed={color === c}
              onClick={() => setColor(c)}
              className="h-7 w-7 rounded-full border-2 border-ink"
              style={{
                background: c,
                boxShadow: color === c ? "0 0 0 3px #fffaf0, 0 0 0 5px #c94f71" : undefined,
              }}
            />
          ))}
        </div>
      </div>

      {categoryOptions.length > 0 && (
      <div className="field">
        <span className="field-label">Categories (pick up to 3) · {categories.length}/3</span>
        <div className="chip-row">
          {categoryOptions.map((cat) => (
            <button
              key={cat}
              type="button"
              aria-pressed={categories.includes(cat)}
              onClick={() => toggleCategory(cat)}
              className={`chip${categories.includes(cat) ? " on" : ""}`}
            >
              {stripEmoji(cat)}
            </button>
          ))}
        </div>
      </div>
      )}

      <label className="check notice items-start">
        <input
          type="checkbox"
          checked={anonymous}
          onChange={(e) => setAnonymous(e.target.checked)}
          className="mt-1"
        />
        <span>
          <span className="flex items-center gap-1 font-bold">
            <Icon name="eye" size={16} /> Post anonymously
          </span>
          <span className="block text-sm">{anonymousHint}</span>
        </span>
      </label>

      <div>
        <button type="button" onClick={post} className="btn" disabled={busy}>
          <Icon name="pin" size={16} /> {busy ? "Posting..." : submitLabel}
        </button>
      </div>
    </div>
  );
}
